import axios from "axios";
import * as Howler from "howler";
import * as Logger from "js-logger";
import { Signal } from "signals";
import Components from "slotworks/components/components";
import { AssetConfigSchema, FileLocation, ManifestSchema } from "slotworks/config/asset-config-schema";
import CanvasManager from "slotworks/graphics/canvas/canvas-manager";
import { ResolutionName } from "slotworks/graphics/canvas/canvas-manager";
import GraphicsManager from "slotworks/graphics/graphics-manager";
import gpuUploader from "slotworks/loader/gpu-uploader";
import { Services } from "slotworks/services/services";
import Contract from "slotworks/utils/contracts/contract";
import Parallel from "slotworks/utils/contracts/parallel";
import Sequence from "slotworks/utils/contracts/sequence";
import DeviceInfo from "slotworks/utils/device-info";
import * as WebFont from "webfontloader";

declare const __VERSION__: string;

class Loader {

    /**
     * Signal for when sounds have been unlocked via a user interaction
     */
    public soundUnlocked: Signal = new Signal();

    /**
     * A minimum amount of time to wait before the initial preloader completes - useful if you have a message in the preloader you want to ensure the user has time to read
     */
    public minPreloaderTime: number = 0;

    /**
     * Set this before preloader in order to include sound load in preloader
     */
    public loadSoundsInPreloader: boolean;

    /**
     * Set this before preloader to fall back to a specific language if the code is missing.
     */
    public fallbackI18nFile: string = "en";

    /**
     * Keep a record of which files belong to which stage, so we can later unload and reload them
     */
    private stages: FileLocation[][];

    /**
     * Keeps a record of which specific textures belong to which stage
     */
    private stageIds: string[][] = [];

    /**
     * Is a stage currently loading
     */
    private stageLoading: boolean;

    /**
     * Queue of stage loads to perform
     */
    private loadStageQueue: Array<() => void> = [];

    /**
     * Keeps a record of which stages have been loaded, and a pixi containr containing all textures loaded
     */
    private loadedStages: Map<number, boolean> = new Map<number, boolean>();

    /**
     * Keep a record of resolves which should be called when a particular stage is loaded
     */
    private stageLoadCallbacks: Map<number, Array<(value: void) => void>> = new Map<number, Array<(value: void) => void>>();

    /**
     * Merged manifest of layout specific and general assets
     */
    private mergedManifest: ManifestSchema;

    /**
     * Optional base URL to be prepended to all urls when loading
     */
    private baseURL: string = "";

    /**
     * Used for debugging RAM consumption
     */
    private predictedRAMConsumption: number = 0;

    private errorSignal: Signal = new Signal();

    public setBaseURL(url: string) {
        this.baseURL = url;
        axios.defaults.baseURL = this.baseURL;
    }

    /**
     * Add a method to call in the event of an error
     */
    public onError(method: (message: string) => void) {
        this.errorSignal.add(method);
    }

    /**
     * Loads the first three stages (the preloader itself is stage 0, then game stage 1 and 2)
     * and displays load progress with a graphical load bar
     *
     * @param manifest {ManifestSchema}
     * @param onLoadProgress {(stage:number, progress:number, resource:PIXI.loaders.Resource)=>{}}
     * @return {Contract<void>}
     */
    public setup(config: AssetConfigSchema): Contract<void> {

        const deviceManifest = config.layouts[DeviceInfo.layoutId].manifest;
        const deviceGraphicsManifest = deviceManifest.graphics;

        this.stages = deviceGraphicsManifest.hd;
        let domElements = deviceManifest.DOMElements.hd;

        if (CanvasManager.resolutionName === ResolutionName.MD) {
            this.stages = deviceGraphicsManifest.md;
            domElements = deviceManifest.DOMElements.md;
        }

        if (CanvasManager.resolutionName === ResolutionName.LD) {
            this.stages = deviceGraphicsManifest.ld;
            domElements = deviceManifest.DOMElements.ld;
        }

        // TODO: Merge all layouts files into above
        // Add files which all devices should load, such as bitmap fonts
        if (config.layouts.all) {
            this.stages[1] = this.stages[1].concat(config.layouts.all.manifest.files);
        }

        this.mergedManifest = config.layouts.all.manifest;

        if (this.stages.length < 3) {
            this.stages[2] = [];
        }

        this.stages.forEach((value, index, array) => {
            if (value === null) {
                array[index] = [];
            }
        });

        const loadSequence = [
            () => this.loadFonts(this.mergedManifest.fonts),
            () => this.loadStage(0),
            () => this.loadDOMElements(domElements)
        ];

        return new Sequence<void>(loadSequence);
    }

    /**
     * Loads stages 1 and 2 and displays load progress with a graphical load bar
     */
    public load(): Contract<void> {

        // Update graphical preloader when a file is loaded
        const preloaderUpdate = (stage: number, progress: number, resource: PIXI.loaders.Resource) => {
            Components.preloader.fileLoaded(resource);
        };

        Components.preloader.init(this.stages[1].concat(this.stages[2]));
        this.hideDOMLoadSpinner();

        const loadParallel: Array<() => Contract<any>> = [
            () => new Sequence([
                () => this.loadStage(1, preloaderUpdate),
                () => this.loadStage(2, preloaderUpdate),
                () => Components.preloader.loadComplete()
            ])
        ];

        if (this.loadSoundsInPreloader) {
            loadParallel.push(() => this.loadSounds());
        }

        if (this.minPreloaderTime) {
            loadParallel.push(() => Contract.getTimeoutContract(this.minPreloaderTime));
        }

        // Load stage 0 (the preloader), then stages 1 and 2
        return new Parallel(loadParallel);
    }

    /**
     * Unloads a stage from memory
     *
     * @param stage {number}
     */
    public unloadStage(stage: number) {
        if (this.stageIds[stage]) {
            while (this.stageIds[stage].length) {
                const textureId = this.stageIds[stage].pop();
                GraphicsManager.destroyTexture(textureId);
            }

            this.loadedStages.delete(stage);

            this.log("Unloaded stage " + stage.toString());
        } else {
            this.log("Could not unload stage. Stage " + stage.toString() + " is not loaded");
        }
    }

    public isStageLoaded(stage: number) {
        return this.loadedStages.has(stage);
    }

    /**
     * Loads a specified stage
     * @param stage {number}
     * @param onLoadProgress {(stage:number, progress:number, resource:PIXI.loaders.Resource)=>{}}
     * @return {Contract<void>}
     */
    public loadStage(stage: number, onLoadProgress?: (stage: number, progress: number, resource: PIXI.loaders.Resource) => void): Contract<void> {

        return new Contract<void>((resolve) => {
            if (this.loadedStages.has(stage)) {
                resolve(null);
            } else if (this.stageLoadCallbacks.has(stage)) {
                this.stageLoadCallbacks.get(stage).push(resolve);
            } else {
                if (this.stageLoading) {
                    this.loadStageQueue.push(() => this.loadStage(stage, onLoadProgress).then(resolve));
                    return;
                }

                const filesToLoad: FileLocation[] = this.stages[stage];
                if (filesToLoad.length === 0) {
                    resolve(null);
                    return;
                }

                const totalSize = Math.round(filesToLoad.reduce((prev, file) => file.size + prev, 0) / 10.24) / 100;

                this.log(`Loading stage ${stage.toString()} (${totalSize} MB)`);

                this.stageLoading = true;
                this.stageLoadCallbacks.set(stage, [resolve]);

                PIXI.loader.pre((resource: PIXI.loaders.Resource, next: Function) => {
                    if (resource.url.indexOf("?") === -1) {
                        resource.url += "?" + __VERSION__;
                    }

                    next();
                });

                PIXI.loader.reset();
                PIXI.loader.removeAllListeners();

                PIXI.loader.baseUrl = this.baseURL;

                PIXI.loader
                    .add(filesToLoad)
                    .on("error", () => {
                        this.errorSignal.dispatch("err.dis", true);
                    })
                    .on("progress", (loader: PIXI.loaders.Loader, resource: PIXI.loaders.Resource) => {
                        if (resource.error) {
                            this.log("Failed to load " + resource.url);
                        } else {
                            this.atlasLoaded(stage, resource);
                            if (onLoadProgress) {
                                onLoadProgress(stage, loader.progress, resource);
                            }
                        }
                    })
                    .load(() => {
                        this.log("Stage " + stage.toString() + " load complete (" + (Math.floor(this.predictedRAMConsumption * 100) / 100) + " MB RAM)");

                        this.stageLoading = false;
                        this.loadedStages.set(stage, true);

                        const loadedResolves = this.stageLoadCallbacks.get(stage);

                        for (const loadedResolve of loadedResolves) {
                            loadedResolve(null);
                        }

                        this.stageLoadCallbacks.delete(stage);

                        if (this.loadStageQueue.length) {
                            this.loadStageQueue.shift()();
                        }
                    });
            }
        });
    }

    public loadSounds(): Contract<void> {
        Services.sound.setLoading();

        if (this.loadSoundsInPreloader) {
            Services.sound.unmute();
        }

        const manifest = this.mergedManifest;

        return new Contract<void>((resolve) => {

            // Disable sounds in IE11, it's very buggy (causes huge lag spikes, see: https://github.com/goldfire/howler.js/issues/931)
            if (DeviceInfo.isIE11()) {
                resolve(null);
                return;
            }

            const soundConfig = manifest.sound;

            soundConfig.src = soundConfig.src.map((src) => this.baseURL + src + "?" + __VERSION__);

            const sound = new Howler.Howl({
                src: soundConfig.src,
                sprite: soundConfig.sprite as IHowlSoundSpriteDefinition,
                autoUnlock: true,
                onunlock: () => this.soundUnlocked.dispatch()
            } as IHowlProperties);

            sound.once("load", () => {
                this.soundLoaded(sound, soundConfig.sprite);
                resolve(null);
            });
        });
    }

    public loadI18n(langCode: string): Contract<any> {
        return new Contract<any>((resolve) => {
            axios.get("i18n/" + langCode + ".json?" + __VERSION__).then((response) => {
                this.log("i18n loaded", response.data);
                resolve(response.data);
            }).catch((e) => {
                if (!e.response) {
                    throw e;
                }
                if (langCode === this.fallbackI18nFile) {
                    Components.alert.error("Translations failed to load", true).execute();
                } else {
                    this.loadI18n(this.fallbackI18nFile).then((data) => {
                        resolve(data);
                    });
                }
            });
        });
    }

    public getStageFiles(stage: number): FileLocation[] {
        return this.stages[stage];
    }

    private loadFonts(fonts: string[]): Contract<void> {
        return new Contract<void>((resolve) => {
            if (!fonts) {
                Logger.warn("No fonts to load");
                resolve(null);
            } else {
                const config = {
                    custom: {
                        families: fonts,
                        urls: [this.baseURL + "font/fonts.css?" + __VERSION__]
                    },
                    active: () => {
                        resolve(null);
                    },
                    fontinactive: (familyName, fvd) => {
                        Logger.warn("Invalid font: " + familyName);
                    },
                    inactive: () => {
                        Logger.warn("Invalid fonts");
                        resolve(null);
                    }
                } as WebFont.Config;

                WebFont.load(config);
            }
        });
    }

    private loadDOMElements(elements: string[]) {
        if (!elements || !elements.length) {
            return Contract.empty();
        }

        const domPreloadElement = document.createElement("div");
        domPreloadElement.id = "DOMPreloadElement";
        domPreloadElement.style.position = "absolute";
        domPreloadElement.style.width = "0";
        domPreloadElement.style.height = "0";
        domPreloadElement.style.overflow = "hidden";
        domPreloadElement.style.zIndex = "-1";

        document.body.appendChild(domPreloadElement);

        let contentString = "";

        return new Contract<void>((resolve) => {
            let imagesToLoad = elements.length;
            const onImageLoaded = () => {
                imagesToLoad--;

                if (imagesToLoad <= 0) {
                    domPreloadElement.style.content = contentString;

                    resolve(null);
                }
            };

            for (const element of elements) {
                const cacheBustedPath = this.baseURL + element + "?" + __VERSION__;
                contentString += "url(" + cacheBustedPath + ") ";

                const img = new Image();
                img.src = cacheBustedPath;
                if (img.complete) {
                    onImageLoaded();
                } else {
                    img.onload = onImageLoaded;
                }
            }
        });
    }

    private soundLoaded(sound: Howl, definition: IHowlSoundSpriteDefinition) {
        Services.sound.setSoundSprite(sound, definition);
    }

    /**
     * When a pixi atlas is loaded, add it to the GraphicsManager so that it can be accessed by the game
     *
     * @param stage {number}
     * @param resource {PIXI.loaders.Resource}
     */
    private atlasLoaded(stage: number, resource: PIXI.loaders.Resource) {
        if (!this.stageIds[stage]) {
            this.stageIds[stage] = [];
        }

        if (resource.textures) {
            for (const id of Object.keys(resource.textures)) {
                GraphicsManager.addTexture(id, resource.textures[id]);
                this.stageIds[stage].push(id);
            }
        } else if (resource.texture) {
            this.predictedRAMConsumption += (resource.texture.baseTexture.realWidth * resource.texture.baseTexture.realHeight * 4) / (1024 * 1024) * 2;

            this.disableMipmapping(resource);
            gpuUploader.upload(resource.texture).then(() => this.log(resource.name + " uploaded to GPU"));
        }
    }

    private disableMipmapping(resource: PIXI.loaders.Resource) {
        if (resource.texture && resource.name.indexOf("nomipmap") >= 0) {
            resource.texture.baseTexture.mipmap = false;
        }
    }

    private hideDOMLoadSpinner() {
        if (document.getElementById("preloadSpinner")) {
            document.getElementById("preloadSpinner").style.display = "none";
        }
    }

    /**
     * Log a message with colored label
     *
     * @param message {string}
     */
    private log(message: string, obj: any = "") {
        Logger.info("%c Loader ", "background: #95f; color: #fff", message, obj);
    }
}

export default new Loader();
